import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef
} from 'react';
import PropTypes from 'prop-types';
import mapboxgl from 'mapbox-gl';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import '@mapbox/mapbox-gl-geocoder/dist/mapbox-gl-geocoder.css';

import clsx from '../clsx';
import { getAccessToken } from './utils';

let geocoderIndex = 1;

const Geocoder = forwardRef((props, ref) => {
  const {
    apiToken,
    mapboxRef,
    id: idProp,
    className,
    // TODO: look into removing this option as `options` is not something supported natively in MapboxGeocoder
    options,
    // All the props that will be passed to the MapboxGeocoder instance
    ...geocoderProps
  } = props;
  const propsRef = useRef(props);
  propsRef.current = props;

  // eslint-disable-next-line no-plusplus
  const id = useMemo(() => idProp || `geocoder-${geocoderIndex++}`, [idProp]);

  const geocoder = useMemo(
    () => {
      return new MapboxGeocoder({
        accessToken: getAccessToken(apiToken),
        mapboxgl,
        flyTo: {
          animate: false
        },
        marker: false,
        ...geocoderProps,
        ...options
      });
    },
    // As we only want to create mapboxgl.Marker once and as we manually update it's props/options,
    // we intentionally have empty dependency array and disabled the eslint rule here.
    //
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useImperativeHandle(ref, () => geocoder, [geocoder]);

  useEffect(() => {
    const onClear = () => {
      propsRef.current?.onClear?.();
    };
    geocoder.on('clear', onClear);

    const onLoading = (e) => {
      propsRef.current?.onLoading?.(e);
    };
    geocoder.on('loading', onLoading);

    const onResults = (e) => {
      propsRef.current?.onResults?.(e);
    };
    geocoder.on('results', onResults);

    const onResult = (e) => {
      propsRef.current?.onResult?.(e);
    };
    geocoder.on('result', onResult);

    const onError = (e) => {
      propsRef.current?.onError?.(e);
    };
    geocoder.on('error', onError);

    // This will also work in cases where the mapboxRef is null due to the Geocoder being rendered without a map.
    document.getElementById(id).appendChild(geocoder.onAdd(mapboxRef));

    return () => {
      geocoder.off('clear', onClear);
      geocoder.off('loading', onLoading);
      geocoder.off('results', onResults);
      geocoder.off('result', onResult);
      geocoder.off('error', onError);
      geocoder.onRemove();
    };
  }, [mapboxRef, geocoder, id]);

  const { language, placeholder } = geocoderProps;

  useEffect(() => {
    geocoder.setLanguage(language);
  }, [geocoder, language]);

  useEffect(() => {
    geocoder.setPlaceholder(placeholder);
  }, [geocoder, placeholder]);

  return <div id={id} className={clsx(className)} />;
});

Geocoder.propTypes = {
  // TODO: rename this prop to accessToken to match with mapbox-gl value once we updated
  // all the places where APIToken is set
  apiToken: PropTypes.string,
  language: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  mapboxRef: PropTypes.shape(),
  id: PropTypes.string,
  options: PropTypes.shape(),
  className: PropTypes.string,
  onClear: PropTypes.func,
  onLoading: PropTypes.func,
  onResults: PropTypes.func,
  onResult: PropTypes.func,
  onError: PropTypes.func
};

Geocoder.defaultProps = {
  apiToken: null,
  mapboxRef: null,
  id: undefined,
  className: undefined,
  options: {},
  onClear: () => {},
  onLoading: () => {},
  onResults: () => {},
  onResult: () => {},
  onError: () => {}
};

export default Geocoder;
